import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import WebSocketClient from '../db/sync/WebSocketClient';
import { colorForEmail, displayNameForEmail } from './colors';
import { getCollaborationSessionId } from './sessionId';

const LIVE_STATE_TTL_MS = 8000;
const LIVE_EMIT_INTERVAL_MS = 100;

const CaseCollaborationContext = createContext(null);

function normalizePresenceUsers(payload, caseId) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.users && (!payload.caseId || payload.caseId === caseId)) {
    return payload.users;
  }

  return [];
}

function upsertUser(prev, nextUser) {
  const key = nextUser.sessionId || nextUser.email;
  const idx = prev.findIndex(
    (u) => (u.sessionId || u.email) === key,
  );
  if (idx >= 0) {
    const copy = [...prev];
    copy[idx] = { ...copy[idx], ...nextUser };
    return copy;
  }
  return [...prev, nextUser];
}

export function CaseCollaborationProvider({ caseId, children }) {
  const currentUserEmail =
    useSelector((state) => state.app.sessionData?.email) || '';
  const currentSessionId = useMemo(() => getCollaborationSessionId(), []);
  const [users, setUsers] = useState([]);
  const [liveByJuror, setLiveByJuror] = useState({});
  const editingJurorRef = useRef(null);
  const lastEmitRef = useRef(0);
  const pendingLiveRef = useRef(null);
  const emitTimerRef = useRef(null);

  const isSelf = useCallback(
    (sessionId) => sessionId === currentSessionId,
    [currentSessionId],
  );

  useEffect(() => {
    if (!caseId) {
      return undefined;
    }

    WebSocketClient.connect().catch((err) => {
      console.warn('Collaboration WebSocket connect failed:', err);
    });

    const unsubPresence = WebSocketClient.on('presence:update', (payload) => {
      setUsers(normalizePresenceUsers(payload, caseId));
    });

    const unsubEditing = WebSocketClient.on('user:editing', (data) => {
      if (data.caseId !== caseId || isSelf(data.sessionId)) {
        return;
      }

      setUsers((prev) =>
        upsertUser(prev, {
          email: data.email,
          sessionId: data.sessionId,
          viewingJuror: data.jurorId,
        }),
      );
    });

    const unsubStopped = WebSocketClient.on('user:stopped', (data) => {
      if (data.caseId !== caseId) {
        return;
      }

      if (!isSelf(data.sessionId)) {
        setUsers((prev) =>
          prev.map((u) =>
            u.sessionId === data.sessionId
              ? { ...u, viewingJuror: null }
              : u,
          ),
        );
      }

      setLiveByJuror((prev) => {
        const jurorLive = prev[data.jurorId];
        if (!jurorLive?.[data.sessionId]) {
          return prev;
        }
        const next = { ...prev };
        const copy = { ...jurorLive };
        delete copy[data.sessionId];
        if (Object.keys(copy).length === 0) {
          delete next[data.jurorId];
        } else {
          next[data.jurorId] = copy;
        }
        return next;
      });
    });

    const unsubLive = WebSocketClient.on('juror:live-state', (data) => {
      if (data.caseId !== caseId || isSelf(data.sessionId)) {
        return;
      }

      setLiveByJuror((prev) => ({
        ...prev,
        [data.jurorId]: {
          ...(prev[data.jurorId] || {}),
          [data.sessionId]: {
            ...data,
            updatedAt: Date.now(),
          },
        },
      }));
    });

    const pruneTimer = setInterval(() => {
      const cutoff = Date.now() - LIVE_STATE_TTL_MS;
      setLiveByJuror((prev) => {
        let changed = false;
        const next = {};

        for (const [jurorId, editors] of Object.entries(prev)) {
          const kept = {};
          for (const [sessionId, state] of Object.entries(editors)) {
            if ((state.updatedAt || 0) >= cutoff) {
              kept[sessionId] = state;
            } else {
              changed = true;
            }
          }
          if (Object.keys(kept).length > 0) {
            next[jurorId] = kept;
          } else {
            changed = true;
          }
        }

        return changed ? next : prev;
      });
    }, 2000);

    return () => {
      unsubPresence();
      unsubEditing();
      unsubStopped();
      unsubLive();
      clearInterval(pruneTimer);
      if (emitTimerRef.current) {
        clearTimeout(emitTimerRef.current);
      }
      if (editingJurorRef.current) {
        WebSocketClient.announceStoppedEditing(
          caseId,
          editingJurorRef.current,
        );
        editingJurorRef.current = null;
      }
    };
  }, [caseId, currentSessionId, isSelf]);

  const flushLiveState = useCallback(() => {
    const payload = pendingLiveRef.current;
    if (!payload || !caseId) {
      return;
    }
    WebSocketClient.emitLiveState({
      ...payload,
      sessionId: currentSessionId,
    });
    lastEmitRef.current = Date.now();
    pendingLiveRef.current = null;
  }, [caseId, currentSessionId]);

  const publishLiveState = useCallback(
    (jurorId, state) => {
      if (!caseId || !jurorId) {
        return;
      }

      pendingLiveRef.current = {
        caseId,
        jurorId,
        ...state,
      };

      const elapsed = Date.now() - lastEmitRef.current;
      if (elapsed >= LIVE_EMIT_INTERVAL_MS) {
        flushLiveState();
        return;
      }

      if (!emitTimerRef.current) {
        emitTimerRef.current = setTimeout(() => {
          emitTimerRef.current = null;
          flushLiveState();
        }, LIVE_EMIT_INTERVAL_MS - elapsed);
      }
    },
    [caseId, flushLiveState],
  );

  const announceEditing = useCallback(
    (jurorId) => {
      if (!caseId) {
        return;
      }

      if (editingJurorRef.current && editingJurorRef.current !== jurorId) {
        WebSocketClient.announceStoppedEditing(
          caseId,
          editingJurorRef.current,
        );
      }

      if (jurorId) {
        WebSocketClient.announceEditing(caseId, jurorId);
        editingJurorRef.current = jurorId;
      } else if (editingJurorRef.current) {
        WebSocketClient.announceStoppedEditing(
          caseId,
          editingJurorRef.current,
        );
        editingJurorRef.current = null;
      }
    },
    [caseId],
  );

  const getEditorsForJuror = useCallback(
    (jurorId) => {
      if (!jurorId) {
        return [];
      }

      return users
        .filter(
          (u) => u.viewingJuror === jurorId && !isSelf(u.sessionId),
        )
        .map((u) => ({
          email: u.email,
          sessionId: u.sessionId,
          displayName: displayNameForEmail(u.email),
          color: colorForEmail(`${u.email}:${u.sessionId}`),
        }));
    },
    [users, isSelf],
  );

  const getLiveStatesForJuror = useCallback(
    (jurorId) => {
      if (!jurorId) {
        return [];
      }

      const states = liveByJuror[jurorId] || {};
      return Object.entries(states)
        .filter(([sessionId]) => !isSelf(sessionId))
        .map(([sessionId, state]) => ({
          sessionId,
          email: state.email,
          displayName: displayNameForEmail(state.email),
          color: colorForEmail(`${state.email}:${sessionId}`),
          ...state,
        }));
    },
    [liveByJuror, isSelf],
  );

  const value = useMemo(
    () => ({
      caseId,
      currentUserEmail,
      currentSessionId,
      users,
      announceEditing,
      publishLiveState,
      getEditorsForJuror,
      getLiveStatesForJuror,
    }),
    [
      caseId,
      currentUserEmail,
      currentSessionId,
      users,
      announceEditing,
      publishLiveState,
      getEditorsForJuror,
      getLiveStatesForJuror,
    ],
  );

  return (
    <CaseCollaborationContext.Provider value={value}>
      {children}
    </CaseCollaborationContext.Provider>
  );
}

export function useCaseCollaboration() {
  const ctx = useContext(CaseCollaborationContext);
  if (!ctx) {
    return {
      caseId: null,
      currentUserEmail: '',
      currentSessionId: '',
      users: [],
      announceEditing: () => {},
      publishLiveState: () => {},
      getEditorsForJuror: () => [],
      getLiveStatesForJuror: () => [],
    };
  }
  return ctx;
}

export default CaseCollaborationContext;
